import {
	colours,
	filterUrlPath,
	fonts,
	fontsLineHeight,
	getColours,
	isFelixMobileUrl,
} from "@helpers";
import { mixpanelTrack } from "@helpers/mixpanel.helper";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import type React from "react";
import styled, { css } from "styled-components";

const buttonStyles = css<{ backgroundcolorprop: string }>`
  align-items: center;
  background-color: ${(props) => getColours(props.backgroundcolorprop)};
  border-radius: 12px;
  border: 2px solid ${(props) => getColours(props.backgroundcolorprop)};
  box-sizing: border-box;
  color: ${(props) =>
		getColours(props.backgroundcolorprop) === colours.MIDNIGHT_BLUE
			? colours.WHITE
			: colours.MIDNIGHT_BLUE} !important;
  display: flex;
  font-family: var(--euclid-medium);
  font-size: ${fonts.BUTTON};
  line-height: ${fontsLineHeight.A};
  justify-content: center;
  outline: none;
  padding: 13px 32px;
  &:focus-visible {
    outline: 2px solid ${colours.MIDNIGHT_BLUE};
    outline-offset: 2px;
  }
  min-width: 193px;
  width: 100%;
  transition: 0.2s;
  @media (min-width: 428px) {
    width: fit-content;
  }
  :hover {
    cursor: pointer;
    border: 2px solid
      ${(props) =>
				getColours(props.backgroundcolorprop) === colours.MIDNIGHT_BLUE
					? colours.MIDNIGHT_BLUE
					: colours.WATERMELON} !important;
    background-color: ${colours.WHITE};
    color: ${colours.MIDNIGHT_BLUE} !important;
  }
  :disabled {
    opacity: 0.5;
  }
  :hover:enabled {
    cursor: pointer;
  }
  :active {
    transition: 0.3s all;
    transform: translateY(3px);
  }
`;

const StyledLink = styled(Link)<{ backgroundcolorprop: string }>`
  ${buttonStyles}
`;

const StyledAnchor = styled.a<{ backgroundcolorprop: string }>`
  ${buttonStyles}
`;

const CTAButton = ({
	children,
	url,
	ctaButtonColour = "Watermelon",
	gtmCtaPosition,
	gtmCtaText,
	onClick,
	preserveAbsoluteUrl,
	...rest
}: {
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
	children: string;
	url: string | undefined;
	ctaButtonColour?: string;
	gtmCtaPosition?: string;
	gtmCtaText?: string;
	preserveAbsoluteUrl?: boolean;
}) => {
	// If preserveAbsoluteUrl is true, keep the full URL (useful for Braze links that should always go to production)
	// Otherwise, convert Felix Mobile URLs to relative paths for environment compatibility
	const searchParams = useSearchParams();

	const urlDestination = preserveAbsoluteUrl
		? url
		: isFelixMobileUrl(url)
			? filterUrlPath(url)
			: url;

	// Carry over current page query params to internal links
	const urlWithParams = (() => {
		if (!urlDestination || !searchParams?.toString()) return urlDestination;

		const isRelativeLink = urlDestination.startsWith("/");
		const isFelixLink = urlDestination.includes("felixmobile.com.au");
		if (!isRelativeLink && !isFelixLink) return urlDestination;

		try {
			const url = new URL(
				urlDestination,
				typeof window !== "undefined"
					? window.location.origin
					: "https://felixmobile.com.au",
			);

			searchParams.forEach((value, key) => {
				if (!url.searchParams.has(key)) {
					url.searchParams.set(key, value);
				}
			});

			return isFelixLink ? url.href : url.pathname + url.search + url.hash;
		} catch {
			return urlDestination;
		}
	})();

	const commonProps = {
		onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
			mixpanelTrack("cta_button_clicked", {
				cta_position: gtmCtaPosition,
				cta_text: gtmCtaText,
				cta_colour: ctaButtonColour,
			});
			onClick && onClick(e);
		},
		style: { textDecoration: "none" },
		backgroundcolorprop: ctaButtonColour,
		"data-gtm-cta-position": gtmCtaPosition,
		"data-gtm-cta-text": gtmCtaText,
		...rest,
	};

	if (urlWithParams.includes("/buy")) {
		return (
			<StyledAnchor href={urlWithParams} {...commonProps}>
				{children}
			</StyledAnchor>
		);
	}

	return (
		<StyledLink href={urlWithParams} {...commonProps}>
			{children}
		</StyledLink>
	);
};

export default CTAButton;
